import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Rescisão → "Prévia da rescisão" (antes do Financeiro, 21/10/2026).
// E-mail ao cliente com a prévia e as verbas variáveis para conferência. O texto vem
// preenchido (nome e vencimento) e é editável; "Gravar como padrão" guarda o texto e o
// Para/Cc do cliente. Anexos livres (prévia, benefícios pagos e descontos, resumo do
// ponto). O Enviar fica só na prévia do e-mail.
const API = process.env.REACT_APP_API_BASE;
const URL_PREVIA = `${API}/api/bporh/rescisao/previa/`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const kb = (n) => (n ? `${Math.max(1, Math.round(n / 1024))} KB` : '');
const LIMITE = 20 * 1024 * 1024;

const EmailPreviaRescisao = ({ clienteId, conveniaId, colaborador, onFechar, onEnviado }) => {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);   // {operador_id, para, cc, assunto, corpo}
  const [arquivos, setArquivos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [ocupado, setOcupado] = useState('');   // '' | 'salvar' | 'restaurar' | 'previa' | 'enviar'
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [previa, setPrevia] = useState(null);
  const arquivoRef = useRef(null);

  const carregar = useCallback((operadorId, manterTexto = false) => {
    setCarregando(true);
    setErro('');
    axios.get(URL_PREVIA, { params: { cliente_id: clienteId, convenia_id: conveniaId, operador_id: operadorId || undefined },
      headers: headers() })
      .then((r) => {
        const x = r.data;
        setD(x);
        setForm((f) => ({
          operador_id: f?.operador_id || x.operador_sugerido_id || '',
          para: f?.para ?? x.para ?? '',
          cc: f?.cc ?? x.cc ?? '',
          assunto: (manterTexto && f?.assunto) || x.assunto || '',
          corpo: (manterTexto && f?.corpo) || x.corpo || '',
        }));
      })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível montar o e-mail'))
      .finally(() => setCarregando(false));
  }, [clienteId, conveniaId]);
  useEffect(() => { carregar(); }, [carregar]);

  const campo = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const corpoBase = () => ({ cliente_id: clienteId, convenia_id: conveniaId, ...form });

  const gravar = () => {
    setOcupado('salvar'); setErro(''); setAviso('');
    axios.post(URL_PREVIA, { acao: 'salvar', ...corpoBase() }, { headers: headers() })
      .then((r) => { setAviso(r.data.mensagem); carregar(form.operador_id, true); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível gravar'))
      .finally(() => setOcupado(''));
  };
  const restaurar = () => {
    setOcupado('restaurar'); setErro(''); setAviso('');
    axios.post(URL_PREVIA, { acao: 'restaurar', cliente_id: clienteId }, { headers: headers() })
      .then((r) => { setAviso(r.data.mensagem); setForm((f) => ({ ...f, assunto: '', corpo: '' })); carregar(form.operador_id); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível restaurar'))
      .finally(() => setOcupado(''));
  };

  const somaArquivos = arquivos.reduce((s, f) => s + f.size, 0);
  const anexar = (ev) => {
    const novos = Array.from(ev.target.files || []);
    setArquivos((a) => [...a, ...novos.filter((n) => !a.some((x) => x.name === n.name && x.size === n.size))]);
    if (arquivoRef.current) arquivoRef.current.value = '';
  };

  const verPrevia = () => {
    if (!arquivos.length) { setErro('Anexe ao menos a prévia da rescisão'); return; }
    if (somaArquivos > LIMITE) { setErro('Os anexos passam de 20 MB'); return; }
    setOcupado('previa'); setErro('');
    axios.post(URL_PREVIA, { acao: 'previa', ...corpoBase() }, { headers: headers() })
      .then((r) => setPrevia(r.data))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível montar a prévia'))
      .finally(() => setOcupado(''));
  };

  const enviar = () => {
    const fd = new FormData();
    Object.entries({ acao: 'enviar', ...corpoBase() }).forEach(([k, val]) => fd.append(k, val ?? ''));
    arquivos.forEach((f) => fd.append('anexos', f));
    setOcupado('enviar'); setErro('');
    axios.post(URL_PREVIA, fd, { headers: headers() })
      .then((r) => { setPrevia(null); if (onEnviado) onEnviado(r.data.mensagem); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível enviar'))
      .finally(() => setOcupado(''));
  };

  const opAtual = (d?.operadores || []).find((o) => String(o.id) === String(form?.operador_id));

  return (
    <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, overflowY: 'auto' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content shadow-lg">
          <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-file-invoice-dollar me-2"></i>Prévia da rescisão ao cliente
              </h5>
              <small style={{ color: '#6c757d' }}>
                {colaborador}{d?.cliente ? ` · ${d.cliente}` : ''}
                {d?.data_desligamento ? ` · desligamento ${d.data_desligamento}` : ''}
              </small>
            </div>
            <button type="button" className="btn-close" onClick={onFechar} disabled={ocupado === 'enviar'}></button>
          </div>
          <div className="modal-body">
            {carregando && !d ? (
              <div className="text-center py-4"><span className="spinner-border text-primary"></span></div>
            ) : !form ? (
              <div className="alert alert-danger mb-0">{erro || 'Não foi possível montar o e-mail'}</div>
            ) : (
              <>
                {d?.vencimento && (
                  <div className="alert alert-light border small py-2">
                    <i className="fas fa-calendar-check me-2 text-primary"></i>
                    Vencimento da rescisão: <b>{d.vencimento}</b>
                    {d.vencimento_regra && <span className="text-muted"> — {d.vencimento_regra}</span>}
                  </div>
                )}
                <div className="row g-2 mb-2">
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1" htmlFor="pr-op">Quem envia</label>
                    <select id="pr-op" className="form-select form-select-sm" value={form.operador_id}
                      onChange={(e) => { setForm((f) => ({ ...f, operador_id: e.target.value })); }}>
                      <option value="">Escolha...</option>
                      {(d.operadores || []).map((o) => (
                        <option key={o.id} value={o.id} disabled={!o.pronto}>
                          {o.nome}{o.pronto ? '' : ' (sem senha de app)'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-8">
                    <label className="form-label small fw-bold mb-1" htmlFor="pr-para">Para — separe por ;</label>
                    <input id="pr-para" className="form-control form-control-sm" value={form.para} onChange={campo('para')}
                      placeholder="rh@cliente.com.br; financeiro@cliente.com.br" />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-bold mb-1" htmlFor="pr-cc">Cc (opcional)</label>
                    <input id="pr-cc" className="form-control form-control-sm" value={form.cc} onChange={campo('cc')} />
                    {(d.grupo_cliente || []).length > 0 && (
                      <div className="small text-muted mt-1">
                        + grupo do cliente em {d.grupo_modo === 'bcc' ? 'cópia oculta' : 'cópia'}: {d.grupo_cliente.join('; ')}
                      </div>
                    )}
                  </div>
                </div>
                <label className="form-label small fw-bold mb-1" htmlFor="pr-assunto">Assunto</label>
                <input id="pr-assunto" className="form-control form-control-sm mb-2" value={form.assunto} onChange={campo('assunto')} />
                <div className="d-flex justify-content-between align-items-end mb-1">
                  <label className="form-label small fw-bold mb-0" htmlFor="pr-corpo">
                    Texto do e-mail {d.texto_proprio && <span className="badge bg-light text-muted border ms-1">texto gravado do cliente</span>}
                  </label>
                  <div className="d-flex gap-2">
                    {d.texto_proprio && (
                      <button type="button" className="btn btn-link btn-sm p-0" onClick={restaurar} disabled={!!ocupado}>
                        restaurar padrão
                      </button>
                    )}
                    <button type="button" className="btn btn-sm btn-outline-primary py-0" onClick={gravar} disabled={!!ocupado}
                      title="Grava o texto e o Para/Cc para as próximas rescisões deste cliente (nome e vencimento viram automáticos)">
                      {ocupado === 'salvar' ? <span className="spinner-border spinner-border-sm me-1"></span>
                        : <i className="fas fa-floppy-disk me-1"></i>}
                      Gravar como padrão
                    </button>
                  </div>
                </div>
                <textarea id="pr-corpo" className="form-control" rows={9} value={form.corpo} onChange={campo('corpo')} />
                <div className="small text-muted mt-1">
                  O nome do colaborador e o vencimento já vêm preenchidos. O rodapé do Time BPO de RH entra no envio.
                </div>

                <div className="border rounded p-2 mt-3" style={{ background: '#f8f9fa' }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="small fw-bold">
                      <i className="fas fa-paperclip me-1"></i>Anexos ({arquivos.length}) {somaArquivos > 0 && <span className="text-muted fw-normal">· {kb(somaArquivos)}</span>}
                    </div>
                    <div>
                      <input ref={arquivoRef} type="file" multiple style={{ display: 'none' }} onChange={anexar}
                        accept={(d.extensoes || []).map((x) => `.${x}`).join(',')} />
                      <button type="button" className="btn btn-sm btn-outline-secondary py-0"
                        onClick={() => arquivoRef.current && arquivoRef.current.click()}>
                        <i className="fas fa-plus me-1"></i>Anexar arquivos
                      </button>
                    </div>
                  </div>
                  <div className="small text-muted mb-1">
                    Prévia da rescisão, relatório de benefícios pagos no mês e descontos, resumo do ponto do mês
                    (PDF, Excel, CSV, Word ou imagem — até 20 MB no total).
                  </div>
                  {arquivos.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="d-flex justify-content-between align-items-center small border-top py-1">
                      <span><i className="fas fa-file me-1 text-muted"></i>{f.name} <span className="text-muted">{kb(f.size)}</span></span>
                      <button type="button" className="btn btn-link btn-sm text-danger p-0"
                        onClick={() => setArquivos((a) => a.filter((_, j) => j !== i))} title="Tirar este anexo">
                        <i className="fas fa-xmark"></i>
                      </button>
                    </div>
                  ))}
                </div>

                {(d.ultimos_envios || []).length > 0 && (
                  <div className="small text-muted mt-2">
                    <i className="fas fa-clock-rotate-left me-1"></i>
                    Último envio: {d.ultimos_envios[0].em} por {d.ultimos_envios[0].operador} para {d.ultimos_envios[0].destinatarios}
                    {(d.ultimos_envios[0].anexos || []).length > 0 && ` (${d.ultimos_envios[0].anexos.join(', ')})`}
                  </div>
                )}
                {aviso && <div className="alert alert-success small py-2 mt-2 mb-0">{aviso}</div>}
                {erro && <div className="alert alert-danger small py-2 mt-2 mb-0">{erro}</div>}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onFechar} disabled={ocupado === 'enviar'}>Cancelar</button>
            <button type="button" className="btn btn-primary" onClick={verPrevia}
              disabled={!form || !!ocupado || !form.para || !form.operador_id}>
              {ocupado === 'previa' ? <span className="spinner-border spinner-border-sm me-2"></span>
                : <i className="fas fa-eye me-2"></i>}
              Visualizar e-mail
            </button>
          </div>
        </div>
      </div>

      {previa && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10010, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-eye me-2"></i>Prévia do e-mail
                  </h5>
                  <small style={{ color: '#6c757d' }}>Confira antes de enviar</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setPrevia(null)} disabled={ocupado === 'enviar'}></button>
              </div>
              <div className="modal-body">
                <table className="table table-sm small mb-2">
                  <tbody>
                    <tr><th style={{ width: 90 }}>De</th><td>{previa.de}</td></tr>
                    <tr><th>Para</th><td>{(previa.para || []).join('; ')}</td></tr>
                    {(previa.cc || []).length > 0 && <tr><th>Cc</th><td>{previa.cc.join('; ')}</td></tr>}
                    {(previa.grupo_cliente || []).length > 0 && (
                      <tr><th>{previa.grupo_modo === 'bcc' ? 'Cco' : 'Cc'} (grupo)</th><td>{previa.grupo_cliente.join('; ')}</td></tr>
                    )}
                    <tr><th>Assunto</th><td className="fw-semibold">{previa.assunto}</td></tr>
                    <tr><th>Anexos</th><td>{arquivos.map((f) => f.name).join(', ')}</td></tr>
                  </tbody>
                </table>
                <iframe title="Prévia do e-mail" srcDoc={previa.html} style={{ width: '100%', height: 420, border: '1px solid #dee2e6', borderRadius: 6 }} />
                {erro && <div className="alert alert-danger small py-2 mt-2 mb-0">{erro}</div>}
              </div>
              <div className="modal-footer">
                <span className="small text-muted me-auto">Sai do Gmail de {opAtual?.nome || 'quem envia'}</span>
                <button type="button" className="btn btn-secondary" onClick={() => setPrevia(null)} disabled={ocupado === 'enviar'}>
                  Voltar e editar
                </button>
                <button type="button" className="btn btn-success fw-bold" onClick={enviar} disabled={ocupado === 'enviar'}>
                  {ocupado === 'enviar' ? <span className="spinner-border spinner-border-sm me-2"></span>
                    : <i className="fas fa-paper-plane me-2"></i>}
                  Enviar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailPreviaRescisao;
